import * as React from 'react';
import { cn } from './cn';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** `flat` sits on paper, `raised` lifts, `outline` is a quiet container. */
  variant?: 'flat' | 'raised' | 'outline';
  /** Adds hover lift + pointer cursor. Use with `asChild`-style wrappers or an inner link. */
  interactive?: boolean;
  /** Selected state for album/photo/template cards. */
  selected?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  as?: 'div' | 'article' | 'section' | 'li' | 'label';
}

const CARD_VARIANT = {
  flat: 'bg-surface border border-line',
  raised: 'bg-surface border border-line shadow-md',
  outline: 'bg-transparent border border-line',
} as const;

const CARD_PADDING = { none: '', sm: 'p-3', md: 'p-4', lg: 'p-6' } as const;

/** Generic container: album tiles, template tiles, settings groups, admin panels. */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = 'flat', interactive, selected, padding = 'md', as: Tag = 'div', className, ...props },
  ref,
) {
  return (
    <Tag
      // @ts-expect-error polymorphic ref
      ref={ref}
      data-selected={selected ? '' : undefined}
      className={cn(
        'relative rounded-lg',
        CARD_VARIANT[variant],
        CARD_PADDING[padding],
        interactive &&
          cn(
            'cursor-pointer transition-[box-shadow,border-color,transform] duration-(--duration-fast) ease-out-soft',
            'hover:-translate-y-0.5 hover:border-line-strong hover:shadow-md',
            'focus-within:border-accent active:translate-y-0',
          ),
        selected && 'border-accent ring-2 ring-accent/40',
        className,
      )}
      {...props}
    />
  );
});

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...props }, ref) {
    return <div ref={ref} className={cn('flex items-start justify-between gap-3', className)} {...props} />;
  },
);

export const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  function CardTitle({ className, ...props }, ref) {
    return <h3 ref={ref} className={cn('truncate text-sm font-semibold text-ink', className)} {...props} />;
  },
);

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(function CardDescription({ className, ...props }, ref) {
  return <p ref={ref} className={cn('text-xs text-ink-2', className)} {...props} />;
});

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...props }, ref) {
    return <div ref={ref} className={cn('flex items-center gap-2 pt-3', className)} {...props} />;
  },
);
